import { useEffect, useState } from 'react';

export const breakpoints = {
  xl: 'only screen and (min-width : 1280px)',
  lg: 'only screen and (min-width : 1025px) and (max-width : 1200px)',
  md: 'only screen and (min-width : 769px) and (max-width : 1024px)',
  sm: 'only screen and (max-width : 768px)',
  xs: 'only screen and (max-width : 640px)',
  minSm: 'only screen and (min-width : 640px)',
  maxXl: 'only screen and (max-width : 1280px)',
  maxLg: 'only screen and (max-width : 1024px)',
  maxMd: 'only screen and (max-width : 768px)',
};
export type Breakpoint = keyof typeof breakpoints;

const getMatches = (query: Breakpoint): boolean => {
  // Prevents SSR issues
  if (typeof window !== 'undefined') {
    return window.matchMedia(getMediaQuery(query)).matches;
  }
  return false;
};

const getMediaQuery = (size: Breakpoint): string => {
  switch (size) {
    case 'xs':
      return breakpoints.xs;
    case 'sm':
      return breakpoints.sm;
    case 'minSm':
      return breakpoints.minSm;
    case 'md':
      return breakpoints.md;
    case 'lg':
      return breakpoints.lg;
    case 'xl':
      return breakpoints.xl;
    case 'maxXl':
      return breakpoints.maxXl;
    case 'maxLg':
      return breakpoints.maxLg;
    case 'maxMd':
      return breakpoints.maxMd;
    default:
      return size;
  }
};

export const useMediaQuery = (query: Breakpoint): boolean => {
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const matchMedia = window.matchMedia(getMediaQuery(query));
      const handleChange = () => setMatches(matchMedia.matches);

      setMatches(matchMedia.matches);

      // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
      if (matchMedia.addListener) {
        matchMedia.addListener(handleChange);
      } else {
        matchMedia.addEventListener('change', handleChange);
      }

      return () => {
        if (matchMedia.removeListener) {
          matchMedia.removeListener(handleChange);
        } else {
          matchMedia.removeEventListener('change', handleChange);
        }
      };
    }
  }, [query]);

  return matches;
};
